import {
  json,
  type LoaderFunctionArgs,
  type HeadersFunction,
} from '@remix-run/node'
import { useLoaderData, useParams } from '@remix-run/react'
import { ClientOnly } from 'remix-utils/client-only'
import * as React from 'react'
import { BackLink } from '~/components/arrow-button.tsx'
import { BlurrableImage } from '~/components/blurrable-image.tsx'
import { GeneralErrorBoundary } from '~/components/error-boundary.tsx'
import { FourHundred, FourOhFour } from '~/components/errors.tsx'
import { Grid } from '~/components/grid.tsx'
import { BlogSection } from '~/components/sections/blog-section.tsx'
import { Spacer } from '~/components/spacer.tsx'
import { H2, H6 } from '~/components/typography.tsx'
import { Disqus } from '~/components/disqus.tsx'
import { getImageBuilder, getImgProps } from '~/images.tsx'
import { type MdxListItem } from '~/types.ts'
import { getBlogRecommendations } from '~/utils/blog.server.ts'
import { getMdxPage } from '~/utils/mdx.server.ts'
import {
  getBannerAltProp,
  getBannerTitleProp,
  mdxPageMeta,
  useMdxComponent,
} from '~/utils/mdx.tsx'
import { requireValidSlug, reuseUsefulLoaderHeaders } from '~/utils/misc.tsx'
import { getServerTimeHeader } from '~/utils/timing.server.ts'

type CatchData = {
  recommendations: Array<MdxListItem>
}

export async function loader({ request, params }: LoaderFunctionArgs) {
  requireValidSlug(params.slug)
  const timings = {}

  const page = await getMdxPage(
    { contentDir: 'blog', slug: params.slug },
    { request, timings },
  )

  const [recommendations] = await Promise.all([
    getBlogRecommendations({
      request,
      timings,
      limit: 3,
      keywords: [
        ...(page?.frontmatter.categories ?? []),
        ...(page?.frontmatter.meta?.keywords ?? []),
      ],
      exclude: [params.slug],
    }),
  ])

  const catchData: CatchData = {
    recommendations,
  }
  const headers = {
    'Cache-Control': 'private, max-age=3600',
    Vary: 'Cookie',
    'Server-Timing': getServerTimeHeader(timings),
  }
  if (!page) {
    throw json(catchData, { status: 404, headers })
  }

  const data = {
    page,
    ...catchData,
  }
  return json(data, { status: 200, headers })
}

export const headers: HeadersFunction = reuseUsefulLoaderHeaders

export const meta = mdxPageMeta

// function ArticleFooter({ editLink }: { editLink: string }) {
//   return (
//     <Grid>
//       <div className="col-span-full mb-12 flex flex-col flex-wrap justify-between gap-2 border-b border-gray-600 pb-12 text-lg font-medium text-slate-500 lg:col-span-8 lg:col-start-3 lg:flex-row lg:pb-6">
//         <div className="flex">
//           <a
//             className="underlined hover:text-black focus:text-black focus:outline-none dark:hover:text-white dark:focus:text-white"
//             target="_blank"
//             rel="noreferrer noopener"
//             href={editLink}
//           >
//             Edit on GitHub
//           </a>
//         </div>
//       </div>
//     </Grid>
//   )
// }

export default function MdxScreen() {
  const data = useLoaderData<typeof loader>()

  const { code, dateDisplay, frontmatter } = data.page
  const params = useParams()
  const { slug } = params
  const Component = useMdxComponent(code)

  const readMarker = React.useRef<HTMLDivElement>(null)
  const isDraft = Boolean(data.page.frontmatter.draft)
  const isArchived = Boolean(data.page.frontmatter.archived)

  return (
    <div key={slug}>
      <Grid className="mb-10 mt-24 lg:mb-24">
        <div className="col-span-full flex justify-between lg:col-span-8 lg:col-start-3">
          <BackLink to="/blog">Back</BackLink>
        </div>
      </Grid>

      <Grid as="header" className="mb-12">
        <div className="col-span-full lg:col-span-8 lg:col-start-3">
          {isDraft ? (
            <div className="prose prose-light mb-6 max-w-full dark:prose-dark">
              {React.createElement(
                'callout-warning',
                {},
                `This blog post is a draft. Please don't share it in its current state.`,
              )}
            </div>
          ) : null}
          {isArchived ? (
            <div className="prose prose-light mb-6 max-w-full dark:prose-dark">
              {React.createElement(
                'callout-warning',
                {},
                `This blog post is archived. It's no longer maintained and may contain outdated information.`,
              )}
            </div>
          ) : null}
          <H2 className="font-mono">{frontmatter.title}</H2>
          <H6 as="p" variant="secondary" className="mt-2">
            {[dateDisplay, data.page.readTime?.text ?? 'quick read']
              .filter(Boolean)
              .join(' — ')}
          </H6>
        </div>
        {frontmatter.bannerCloudinaryId ? (
          <div className="col-span-full mt-10 lg:col-span-10 lg:col-start-2 lg:mt-16">
            <BlurrableImage
              key={frontmatter.bannerCloudinaryId}
              blurDataUrl={frontmatter.bannerBlurDataUrl}
              className="aspect-[3/4] md:aspect-[3/2]"
              img={
                <img
                  key={frontmatter.bannerCloudinaryId}
                  title={getBannerTitleProp(frontmatter)}
                  {...getImgProps(
                    getImageBuilder(
                      frontmatter.bannerCloudinaryId,
                      getBannerAltProp(frontmatter),
                    ),
                    {
                      className: 'rounded-lg object-cover object-center',
                      widths: [280, 560, 840, 1100, 1650, 2500, 2100, 3100],
                      sizes: [
                        '(max-width:1023px) 80vw',
                        '(min-width:1024px) and (max-width:1620px) 67vw',
                        '1100px',
                      ],
                      transformations: {
                        background: 'rgb:e6e9ee',
                      },
                    },
                  )}
                />
              }
            />
          </div>
        ) : null}
      </Grid>

      <main ref={readMarker}>
        <Grid className="prose prose-light mb-24 break-words dark:prose-dark">
          <Component />
        </Grid>
      </main>

      {/* <ArticleFooter editLink={data.page.editLink} /> */}
      <ClientOnly fallback={<div>Loading comments...</div>}>
        {() => (
          <Disqus
            identifier={frontmatter.id}
            title={frontmatter.title!}
            url={window.location.href}
          />
        )}
      </ClientOnly>

      <Spacer size="base" />

      <BlogSection
        articles={data.recommendations}
        title="Suggested Readings"
        description=""
        showArrowButton={false}
      />
    </div>
  )
}

export function ErrorBoundary() {
  return (
    <GeneralErrorBoundary
      statusHandlers={{
        400: ({ error }) => <FourHundred error={error.statusText} />,
        404: ({ error }) => (
          <FourOhFour articles={error.data.recommendations} />
        ),
      }}
    />
  )
}
